<script lang="ts" setup>
import type { Swiper as SwiperClass } from 'swiper/types'
import accVideo from '@/assets/features/acc.mp4'
import bassVideo from '@/assets/features/bass.mp4'
import drumsVideo from '@/assets/features/drums.mp4'
import eleVideo from '@/assets/features/ele.mp4'
import keysVideo from '@/assets/features/keys.mp4'
import pianoVideo from '@/assets/features/piano.mp4'
import strefaInspiracjiImg from '@/assets/strefa-inspiracji.webp'
import { TutorialVideoType } from '@/components/ui/types'
import { useApi } from '@/composables/useApi'

const {
  getApiUrl
} = useApi()

const slider = ref<SwiperClass>()

const getRelativePosition = (currentIndex: number, index: number, totalItems: number) => {
  let diff = currentIndex - index

  if (diff > totalItems / 2) {
    diff = diff - totalItems
  } else if (diff < -totalItems / 2) {
    diff = diff + totalItems
  }
  return diff
}

const getCardRotation = (relativePosition: number, slidesPerView: number) => {
  const rotations = [-12, 0, 12]
  const visualIndex = Math.abs(relativePosition) % slidesPerView
  return rotations[visualIndex]
}

const { fetchTutorials } = useTutorials()
const { data: tutorials } = fetchTutorials()

const selectedVideo = ref<TutorialVideoType>('piano')
const videos: Record<TutorialVideoType, string> = {
  acoustic_guitar: accVideo,
  bass_guitar: bassVideo,
  drums: drumsVideo,
  electric_guitar: eleVideo,
  keys: keysVideo,
  piano: pianoVideo,
}
const video = computed(() => videos[selectedVideo.value])
</script>

<template>
  <v-container id="wszystko-w-jednym-miejscu">
    <div class="d-flex flex-column ga-5">
      <div class="mb-1 text-center">
        <v-chip
          color="gray-300"
          variant="outlined"
          text="CSM/online"
        />
      </div>
      <div class="text-h3 text-center">
        Wszystko w jednym miejscu
      </div>
      <div
        class="text-subtitle-1 text-gray-500 font-weight-regular mx-auto text-center"
        :style="{ maxWidth: '580px'}"
      >
        Gotowe materiały, które sprawiają, że przygotowanie zespołu do uwielbienia staje się prostsze i bardziej skuteczne.
      </div>

      <div>
        <swiper
          navigation
          loop
          :space-between="16"
          @swiper="(swiper) => slider = swiper"
        >
          <swiper-slide class="h-auto d-flex align-center justify-center overflow-hidden">
            <div class="d-flex flex-column ga-8 py-8">
              <div
                class="py-12 position-relative mx-auto"
                :style="{ maxWidth: $vuetify.display.mdAndUp ? '620px' : undefined }"
              >
                <v-btn
                  v-if="$vuetify.display.mdAndUp"
                  color="primary-800"
                  text="Baza pieśni"
                  prepend-icon="mdi-music-box-multiple-outline"
                  variant="text"
                  class="font-weight-bold bg-gray-25 border-gray-200 border-opacity-100 elevation-2"
                  border
                  :href="getApiUrl('tutorials')"
                  size="x-large"
                  :style="{
                    position: 'absolute',
                    left: '-150px',
                    top: 0,
                    bottom: 0,
                    margin: 'auto 0',
                    zIndex: 2,
                  }"
                />
                <ui-cards-swiper
                  :autoplay="{
                    delay: 2000,
                    disableOnInteraction: false,
                  }"
                  :items="tutorials!.selected.map((tutorial) => ({
                    src: tutorial.image.url,
                    type: 'image',
                  }))"
                  :slides-per-view="3"
                >
                  <template #card="{ item, index, currentIndex }">
                    <ui-card
                      :src="item.src"
                      :type="item.type"
                      :rotate="getCardRotation(getRelativePosition(currentIndex, index, tutorials!.selected.length), 3)"
                      :size="248"
                      :border="12"
                    />
                  </template>
                </ui-cards-swiper>
              </div>
              <div
                class="d-flex flex-column ga-1 text-subtitle-1 mx-auto text-center px-6"
                :style="{ maxWidth: '650px' }"
              >
                <div class="font-weight-medium">
                  300+ opracowań polskich i zagranicznych utworów
                </div>
                <div class="text-gray-500 font-weight-regular">
                  Potrzebujesz akordów, tekstów czy nut dla wokalistów? Mamy to!
                  Każdego miesiąca nasza baza powiększa się o nowe pieśni uwielbienia.
                </div>
              </div>
            </div>
          </swiper-slide>

          <swiper-slide class="h-auto d-flex align-center justify-center">
            <div class="d-flex flex-column ga-8 py-6 w-100">
              <div class="pb-8">
                <div
                  class="position-relative mx-auto w-100"
                  :style="{ maxWidth: '800px' }"
                >
                  <div
                    v-if="$vuetify.display.mdAndUp"
                    class="d-flex flex-column align-end ga-4"
                    :style="{
                      position: 'absolute',
                      left: 0,
                      transform: 'translateX(-50%)',
                      top: '40px',
                      zIndex: 2,
                    }"
                  >
                    <div>
                      <v-btn
                        color="primary-800"
                        text="Tutoriale wideo"
                        prepend-icon="mdi-music-box-multiple-outline"
                        variant="text"
                        class="font-weight-bold bg-gray-25 border-gray-200 border-opacity-100 elevation-2"
                        :href="getApiUrl('tutorials')"
                        border
                        size="x-large"
                      />
                    </div>
                    <div>
                      <v-btn
                        text="Ulubione"
                        prepend-icon="mdi-heart-outline"
                        variant="text"
                        class="font-weight-bold bg-gray-25 border-gray-200 border-opacity-100 elevation-2"
                        :href="getApiUrl('#menu/favorites')"
                        border
                      />
                    </div>
                  </div>
                  <div
                    v-if="$vuetify.display.mdAndUp"
                    class="d-flex flex-column justify-center align-end ga-4"
                    :style="{
                      position: 'absolute',
                      right: 0,
                      transform: 'translateX(60%)',
                      top: 0,
                      bottom: 0,
                      zIndex: 2,
                    }"
                  >
                    <v-card
                      class="font-weight-bold bg-gray-25 border-gray-200 border-opacity-100 elevation-2 pa-6"
                      border
                      rounded="xll"
                    >
                      <ui-informations
                        :value="{
                          'Tonacja': [
                            { text: 'B' },
                          ],
                          'Tempo': [
                            { text: '65 BPM' },
                          ],
                          'Tagi': [
                            { text: 'Poddanie' },
                            { text: 'Zaufanie' },
                            { text: 'Polskie uwielbienie' },
                          ],
                        }"
                      />
                    </v-card>
                  </div>
                  <v-fade-transition mode="out-in">
                    <csm-ui-video
                      :key="video"
                      class="mx-auto"
                      :url="{ 'video/mp4': video }"
                      width="100%"
                      :aspect-ratio="16/9"
                      rounded="xl"
                    />
                  </v-fade-transition>
                  <div
                    class="position-absolute d-flex justify-center w-100"
                    :style="{
                      transform: 'translateY(50%)',
                      bottom: 0,
                      left: 0,
                      right: 0,
                    }"
                  >
                    <div
                      class="bg-gray-25 d-inline-block rounded-lg elevation-2"
                      :style="{
                        transform: $vuetify.display.xs ? 'scale(0.8)' : undefined,
                      }"
                    >
                      <ui-video-icons-select v-model="selectedVideo" />
                    </div>
                  </div>
                </div>
              </div>
              <div
                class="d-flex flex-column ga-1 text-subtitle-1 mx-auto text-center px-6"
                :style="{ maxWidth: '700px' }"
              >
                <div class="font-weight-medium">
                  Naucz się swoich partii bez stresu i zgadywania
                </div>
                <div class="text-gray-500 font-weight-regular">
                  Nasi nauczyciele przeprowadzą cię krok po kroku przez każdą sekcję piosenki
                  i zasugerują najlepsze brzmienia dla twojego instrumentu.
                </div>
              </div>
            </div>
          </swiper-slide>

          <swiper-slide class="h-auto d-flex align-center justify-center">
            <div class="d-flex flex-column ga-8 py-6 w-100">
              <div
                class="position-relative mx-auto w-100 mb-12"
                :style="{ maxWidth: '800px' }"
              >
                <div
                  v-if="$vuetify.display.mdAndUp"
                  class="d-flex flex-column align-start ga-4"
                  :style="{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    transform: 'translateX(-30%)',
                    zIndex: 2,
                  }"
                >
                  <div>
                    <v-btn
                      text="Materiały z CSM/conf"
                      prepend-icon="mdi-plus"
                      variant="text"
                      class="font-weight-bold bg-gray-25 border-gray-200 border-opacity-100 elevation-2"
                      :href="getApiUrl('inspirations')"
                      border
                    />
                  </div>
                  <div>
                    <v-btn
                      text="Wykłady"
                      prepend-icon="mdi-plus"
                      variant="text"
                      class="font-weight-bold bg-gray-25 border-gray-200 border-opacity-100 elevation-2"
                      :href="getApiUrl('inspirations')"
                      border
                    />
                  </div>
                </div>
                <v-no-ssr>
                  <div
                    v-if="$vuetify.display.mdAndUp"
                    class="d-flex flex-column justify-center align-end ga-4"
                    :style="{
                      position: 'absolute',
                      right: 0,
                      transform: 'translateX(50%)',
                      top: 0,
                      bottom: 0,
                      zIndex: 2,
                    }"
                  >
                    <div>
                      <v-btn
                        color="primary-800"
                        text="Strefa Inspiracji"
                        prepend-icon="mdi-chart-timeline-variant-shimmer"
                        variant="text"
                        class="font-weight-bold bg-gray-25 border-gray-200 border-opacity-100 elevation-2"
                        :href="getApiUrl('inspirations')"
                        border
                        size="x-large"
                      />
                    </div>
                  </div>
                  <div
                    class="d-flex justify-center"
                    :style="{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      bottom: 0,
                      zIndex: 2,
                      transform: 'translateY(40%)',
                    }"
                  >
                    <v-card
                      class="font-weight-bold bg-gray-25 border-gray-200 border-opacity-100 elevation-2 pa-5"
                      width="100%"
                      :max-width="480"
                      rounded="xl"
                    >
                      <csm-ui-audio-player disabled />
                    </v-card>
                  </div>
                </v-no-ssr>
                <v-img
                  :aspect-ratio="16/8"
                  :src="strefaInspiracjiImg"
                  cover
                  rounded="xll"
                />
              </div>
              <div
                class="d-flex flex-column ga-1 text-subtitle-1 mx-auto text-center px-6"
                :style="{ maxWidth: '720px' }"
              >
                <div class="font-weight-medium">
                  100+ godzin wykładów, warsztatów i uwielbienia
                </div>
                <div class="text-gray-500 font-weight-regular">
                  Czyli mnóstwo inspiracji i praktycznej wiedzy, jak budować służbę uwielbienia,
                  wspierać zespół i rozwijać się jako lider, wokalista czy instrumentalista.
                </div>
              </div>
            </div>
          </swiper-slide>
        </swiper>
      </div>
      <div class="d-flex flex-row align-center justify-center ga-5">
        <v-btn
          color="gray-500"
          class="bg-gray-100"
          icon="mdi-arrow-left"
          variant="text"
          @click="() => slider?.slidePrev()"
        />
        <csm-ui-dots-pagination
          :model-value="slider?.realIndex"
          bg-color="gray-100"
          :length="slider?.slides.length || 0"
          @update:model-value="(val: number) => slider?.slideToLoop(val)"
        />
        <v-btn
          color="gray-500"
          class="bg-gray-100"
          icon="mdi-arrow-right"
          variant="text"
          @click="() => slider?.slideNext()"
        />
      </div>
    </div>
  </v-container>
</template>
